import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowUpDown, CheckCircle2, Delete, Gem, Search } from 'lucide-react'
import AnimatedButton from '../../../components/AnimatedButton'
import useAudioManager from '../../../hooks/useAudioManager'
import { hapticImpact, hapticNotify, hapticSelectionChanged } from '../../../utils/haptic'

const MIN_BET_TOTAL = 100

const RoyaleDesktopBetPanel = ({
  isDisabled,
  isLoading,
  balance,
  inventory,
  onLoadInventory,
  onSubmit
}) => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { playSound } = useAudioManager({
    pick: 'pick'
  })

  const [crystalValue, setCrystalValue] = useState('')
  const [selectedIds, setSelectedIds] = useState([])
  const [searchQuery, setSearchQuery] = useState('')
  const [sortOrder, setSortOrder] = useState('desc')
  const [errorText, setErrorText] = useState('')

  useEffect(() => {
    onLoadInventory?.()
  }, [onLoadInventory])

  useEffect(() => {
    const availableIds = new Set((inventory || []).map((item) => item.id))
    setSelectedIds((prev) => prev.filter((itemId) => availableIds.has(itemId)))
  }, [inventory])

  const normalizeCrystalValue = (value) => {
    if (value === '' || value === null || typeof value === 'undefined') {
      return ''
    }

    const numericValue = Number(value)
    const safeBalance = Math.max(0, Number(balance || 0))

    if (!Number.isFinite(numericValue)) {
      return ''
    }

    return String(Math.max(0, Math.min(numericValue, safeBalance)))
  }

  const parsedCrystal = Number(crystalValue || 0)

  const selectedItems = useMemo(() => {
    return (inventory || []).filter((item) => selectedIds.includes(item.id))
  }, [inventory, selectedIds])

  const itemsTotal = useMemo(() => {
    return selectedItems.reduce((sum, item) => sum + Number(item.price ?? item.cost ?? 0), 0)
  }, [selectedItems])

  const totalBet = parsedCrystal + itemsTotal
  const hasBetInput = parsedCrystal > 0 || selectedIds.length > 0
  const missingAmount = Math.max(0, MIN_BET_TOTAL - totalBet)
  const canSubmit = totalBet >= MIN_BET_TOTAL && !isDisabled && !isLoading

  const filteredInventory = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase()
    const baseList = normalizedQuery
      ? (inventory || []).filter((item) => String(item.name || '').toLowerCase().includes(normalizedQuery))
      : (inventory || [])

    return [...baseList].sort((a, b) => {
      const priceA = Number(a.cost || 0)
      const priceB = Number(b.cost || 0)
      return sortOrder === 'asc' ? priceA - priceB : priceB - priceA
    })
  }, [inventory, searchQuery, sortOrder])

  const handleQuickCrystal = (value) => {
    playSound('pick')
    hapticImpact('light')
    setCrystalValue(normalizeCrystalValue(value))
  }

  const toggleItem = (itemId) => {
    playSound('pick')
    hapticSelectionChanged()
    setSelectedIds((prev) => (
      prev.includes(itemId)
        ? prev.filter((id) => id !== itemId)
        : [...prev, itemId]
    ))
  }

  const clearForm = () => {
    setCrystalValue('')
    setSelectedIds([])
    setSearchQuery('')
    setSortOrder('desc')
    setErrorText('')
  }

  const handleSubmit = async () => {
    setErrorText('')

    if (isDisabled) {
      hapticNotify('error')
      setErrorText(t('royale.buttons.betsClosed'))
      return
    }

    if (!parsedCrystal && selectedItems.length === 0) {
      hapticNotify('error')
      setErrorText(t('royale.betModal.errorNoSelection'))
      return
    }

    if (totalBet < MIN_BET_TOTAL) {
      hapticNotify('error')
      setErrorText(t('royale.betModal.missingAmount', { amount: missingAmount.toLocaleString() }))
      return
    }

    if (parsedCrystal < 0 || parsedCrystal > Number(balance || 0)) {
      hapticNotify('error')
      setErrorText(t('royale.betModal.errorExceedsBalance'))
      return
    }

    hapticImpact('medium')

    try {
      const isSuccess = await onSubmit?.({
        crystal: parsedCrystal,
        items: selectedIds,
        total: totalBet
      })

      if (isSuccess) {
        clearForm()
      }
    } catch (error) {
      // Ошибки отправки уже обрабатываются в родительском компоненте
    }
  }

  return (
    <div className="rounded-2xl border border-white/10 bg-surface/50 p-4 space-y-4">
      <h3 className="text-white font-semibold">{t('royale.desktopBet.title')}</h3>

      <div>
        <div className="mb-2 flex items-center justify-between text-sm">
          <span className="text-text-secondary">{t('royale.betModal.crystalsTitle')}</span>
          <span className="inline-flex items-center gap-1 text-white font-semibold">
            <Gem className="w-4 h-4 text-accent-primary" />
            {Number(balance || 0).toLocaleString()}
          </span>
        </div>

        {Number(balance || 0) > 0 ? (
          <>
            <input
              type="number"
              min="0"
              value={crystalValue}
              onChange={(event) => setCrystalValue(normalizeCrystalValue(event.target.value))}
              placeholder={t('royale.betModal.inputPlaceholder')}
              className="w-full rounded-lg border border-white/10 bg-dark-primary px-3 py-2 text-white outline-none"
            />

            <div className="mt-2 grid grid-cols-4 gap-1.5">
              {[100, 500, 1000, 10000].map((value) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => handleQuickCrystal(parsedCrystal + value)}
                  className="rounded-md border border-white/10 bg-dark-primary px-2 py-1 text-xs text-text-secondary hover:text-white"
                >
                  +{value}
                </button>
              ))}

              {[
                { label: '1/4', value: 0.25 },
                { label: '1/2', value: 0.5 },
                { label: '3/4', value: 0.75 }
              ].map((option) => (
                <button
                  key={option.label}
                  type="button"
                  onClick={() => handleQuickCrystal(Math.floor(Number(balance || 0) * option.value))}
                  className="rounded-md border border-white/10 bg-dark-primary px-2 py-1 text-xs text-text-secondary hover:text-white"
                >
                  {option.label}
                </button>
              ))}

              <button
                type="button"
                onClick={() => handleQuickCrystal(balance)}
                className="rounded-md border border-white/10 bg-dark-primary px-2 py-1 text-xs text-text-secondary hover:text-white"
              >
                {t('royale.betModal.maxLabel')}
              </button>

              <button
                type="button"
                onClick={() => handleQuickCrystal('')}
                className="rounded-md border border-white/10 bg-dark-primary px-2 py-1 text-xs text-text-secondary hover:text-white inline-flex items-center justify-center"
                aria-label={t('royale.betModal.clearCrystals')}
                title={t('royale.betModal.clearCrystals')}
              >
                <Delete className="w-3.5 h-3.5" />
              </button>
            </div>
          </>
        ) : (
          <button
            type="button"
            onClick={() => navigate('/payment')}
            className="w-full rounded-lg border border-white/10 bg-dark-primary px-3 py-2 text-text-secondary hover:text-white"
          >
            {t('royale.betModal.topUpBalance')}
          </button>
        )}
      </div>

      {inventory.length > 0 && (
        <div>
          <div className="mb-2 flex items-center justify-between">
            <p className="text-sm text-text-secondary">{t('royale.betModal.itemsTitle')}</p>

            <button
              type="button"
              onClick={() => {
                hapticSelectionChanged()
                setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
              }}
              className="rounded-md border border-white/10 bg-dark-primary p-1.5 text-text-secondary hover:text-white"
            >
              <ArrowUpDown className="w-4 h-4" />
            </button>
          </div>

          <div className="relative mb-2">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-secondary" />
            <input
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              placeholder={t('royale.betModal.searchPlaceholder')}
              className="w-full rounded-lg border border-white/10 bg-dark-primary py-2 pl-9 pr-3 text-white outline-none"
            />
          </div>

          <div className="grid max-h-56 grid-cols-4 gap-2 overflow-y-auto pr-1">
            {filteredInventory.map((item) => {
              const isSelected = selectedIds.includes(item.id)
              const itemPrice = Number(item.price ?? item.cost ?? 0)

              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => toggleItem(item.id)}
                  className={`relative rounded-lg border bg-dark-primary transition-all ${
                    isSelected
                      ? 'border-accent-primary shadow-md shadow-accent-primary/20'
                      : 'border-white/10 hover:border-white/20'
                  }`}
                >
                  {isSelected && (
                    <div className="absolute left-1 top-1 z-10">
                      <div className="flex h-4 w-4 items-center justify-center rounded-full bg-accent-primary">
                        <CheckCircle2 className="h-3 w-3 text-dark-primary" />
                      </div>
                    </div>
                  )}

                  <div className="aspect-square p-1.5">
                    <img src={item.img} alt={item.name} className="h-full w-full object-contain" loading="lazy" />
                  </div>

                  <div className="px-1.5 pb-1.5 text-center">
                    <p className="truncate text-[10px] text-white">{String(item.name || '').split('|')[0].trim()}</p>
                    <p className="mt-1 inline-flex w-full items-center justify-center gap-1 text-[10px] font-bold text-accent-primary">
                      <Gem className="h-3 w-3" />
                      {itemPrice.toLocaleString()}
                    </p>
                  </div>
                </button>
              )
            })}
          </div>
        </div>
      )}

      {hasBetInput && (
        <>
          {errorText && <p className="text-sm text-red-400">{errorText}</p>}

          <AnimatedButton onClick={handleSubmit} disabled={!canSubmit}>
            {isLoading
              ? t('royale.betModal.submitting')
              : isDisabled
                ? t('royale.buttons.betsClosed')
                : totalBet < MIN_BET_TOTAL
                  ? t('royale.betModal.missingAmount', { amount: missingAmount.toLocaleString() })
                  : (
                    <span className="inline-flex items-center gap-2">
                      {t('royale.betModal.submit')}
                      <span className="inline-flex items-center gap-1">
                        <Gem className="w-4 h-4 text-dark-primary" />
                        {totalBet.toLocaleString()}
                      </span>
                    </span>
                  )}
          </AnimatedButton>
        </>
      )}
    </div>
  )
}

export default RoyaleDesktopBetPanel
